import React, { FC, useEffect, useState } from 'react';
import {
	Icon,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Tooltip,
	Typography,
} from '@mui/material';
import styled from 'styled-components';
import NoResults from '@Components/NoResults/NoResults';
import { useAppThunkDispatch, useTypedSelector } from '@Features/store';
import { FlexLayout } from '@Styled/utilities';
import { videoProvidersUsageFetch } from '../Slices/VideoProviderSettings.slice';

/** Bytes -> GB with 2 decimals; null/unknown -> 'n/a'. */
const toGb = (bytes: number | null | undefined) => {
	if (bytes === null || bytes === undefined) return 'n/a';
	return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
};

/**
 * Per-instructor provider usage. Local pagination on purpose — two URL-driven
 * MainTables on one page would fight over ?page. The usage endpoint's meta is
 * {total, page, per_page}.
 */
const UsageTable: FC = () => {
	const dispatch = useAppThunkDispatch();
	const { usage, loaders } = useTypedSelector(
		(state) => state.VideoProviderSettings
	);
	const [page, setPage] = useState(0);
	const [perPage, setPerPage] = useState(10);

	useEffect(() => {
		dispatch(videoProvidersUsageFetch({ page: page + 1, per_page: perPage }));
	}, [dispatch, page, perPage]);

	return (
		<Section>
			<FlexLayout alignItems="center" gridGap="0.5rem">
				<Typography variant="h6">Usage per instructor</Typography>
				<Tooltip title="Refresh">
					<span>
						<IconButton
							size="small"
							disabled={loaders.usage === 'pending'}
							onClick={() =>
								dispatch(
									videoProvidersUsageFetch({
										page: page + 1,
										per_page: perPage,
									})
								)
							}
						>
							<Icon fontSize="small">refresh</Icon>
						</IconButton>
					</span>
				</Tooltip>
			</FlexLayout>
			<TableContainer component={Paper} variant="outlined">
				<Table size="small">
					<TableHead>
						<HeadRow>
							<TableCell>Instructor</TableCell>
							<TableCell>Bunny Storage</TableCell>
							<TableCell>Bunny Streaming</TableCell>
							<TableCell>Bunny Videos</TableCell>
							<TableCell>Vdo Streaming</TableCell>
							<TableCell>Vdo Storage (approx)</TableCell>
							<TableCell>Vdo Videos</TableCell>
						</HeadRow>
					</TableHead>
					<TableBody>
						<CaptionRow>
							<TableCell colSpan={7}>
								Measurement windows differ per provider: Bunny streaming is
								month-to-date from the library object; VdoCipher streaming uses
								daily-final accounting (about a day behind, last 30 days) and
								shows n/a while unavailable; VdoCipher storage is approximated
								from imported source file sizes.
							</TableCell>
						</CaptionRow>
						{usage.list.length === 0 && (
							<TableRow>
								<TableCell colSpan={7}>
									<NoResults />
								</TableCell>
							</TableRow>
						)}
						{usage.list.map((row) => (
							<TableRow key={row.instructor_id} hover>
								<TableCell>
									{row.instructor_name} (#{row.instructor_id})
								</TableCell>
								<TableCell>
									{row.bunny ? toGb(row.bunny.storage_bytes) : 'n/a'}
								</TableCell>
								<TableCell>
									{row.bunny ? toGb(row.bunny.streaming_bytes) : 'n/a'}
								</TableCell>
								<TableCell>{row.bunny ? row.bunny.video_count : 'n/a'}</TableCell>
								<TableCell>
									{row.vdocipher ? toGb(row.vdocipher.streaming_bytes) : 'n/a'}
								</TableCell>
								<TableCell>
									{row.vdocipher
										? toGb(row.vdocipher.storage_bytes_approx)
										: 'n/a'}
								</TableCell>
								<TableCell>
									{row.vdocipher ? row.vdocipher.video_count : 'n/a'}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
			<TablePagination
				component="div"
				count={usage.meta?.total ?? 0}
				page={page}
				onPageChange={(event, newPage) => setPage(newPage)}
				rowsPerPage={perPage}
				onRowsPerPageChange={(event) => {
					setPerPage(Number(event.target.value));
					setPage(0);
				}}
				rowsPerPageOptions={[10, 25, 50]}
			/>
		</Section>
	);
};

const Section = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
`;

const HeadRow = styled(TableRow)`
	th {
		text-transform: uppercase;
		font-weight: 600;
		white-space: nowrap;
	}
`;

const CaptionRow = styled(TableRow)`
	td {
		color: rgba(0, 0, 0, 0.6);
		font-size: 0.75rem;
	}
`;

export default UsageTable;
